import { HttpEventType } from '@angular/common/http';
import { Component, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import moment from 'moment';
import { Popover } from 'primeng/popover';
import readXlsxFile from 'read-excel-file';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { NoteTypeEnums } from 'src/app/enum/note-type-enums';
import { NoteImportModel } from 'src/app/models/imports/note-import.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { WindowService } from 'src/app/services/authentication/window.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { NoteService } from 'src/app/services/invoicing/note.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { EnterpriseService } from 'src/app/services/management/enterprise.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-notes-import',
  templateUrl: './notes-import.component.html',
  styleUrls: ['./notes-import.component.scss'],
  standalone: false
})
export class NotesImportComponent implements OnInit {

  private readonly branchService = inject(BranchService);
  private readonly companyService = inject(CompanyService);
  private readonly enterpriseService = inject(EnterpriseService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly noteService = inject(NoteService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);

  @ViewChild('information') informationPopover!: Popover;
  branchCode: number = 0;
  branchId: number = 0;
  companyCode: number = 0;
  companyId: number = 0;
  errorNotes: number = 0;
  enterpriseCode: number = 0;
  enterpriseId: number = 0;
  eventFile: any;
  formImport: FormGroup = new FormGroup({});
  isOpened: string | undefined = undefined;
  listBranches: any[] = [];
  listCompanies: any[] = [];
  listEnterprises: any[] = [];
  loadPreInvoicingModel: LoadPreInvoicingModel = new LoadPreInvoicingModel();
  nameFile: string = 'Ningún archivo seleccionado.';
  nameWindow: string = 'Importación Notas';
  notesAlerts: Array<PreInvoicingAlertModel> = new Array<PreInvoicingAlertModel>;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  url: string = "notes-import";
  visibleTxt: boolean = false;

  async ngOnInit() {
    this.validateForm();
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    await this.getPermission();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  attach() {
    document.getElementById('fileImport')?.click();
  }

  clearInputFile() {
    this.nameFile = 'Ningún archivo seleccionado.';
    if (this.eventFile) {
      this.eventFile.target.value = '';
    }
  }

  downloadErrorsTxt() {
    if (!this.notesAlerts || this.notesAlerts.length === 0) {
      this.toastHandlerService.showToast(
        'error',
        'No hay alertas para exportar'
      );
      return;
    }

    this.noteService.exportImportErrors(this.notesAlerts).subscribe({
      next: (blob: Blob) => {
        const file = new Blob([blob], { type: 'text/plain' });
        const url = window.URL.createObjectURL(file);
        const today = new Date();
        const formatDate = today.toISOString().split('T')[0];

        const a = document.createElement('a');
        a.href = url;
        a.download = `ErroresImportacionNotas_${formatDate}.txt`;
        a.click();

        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastHandlerService.showToast(
          'error',
          'Error al exportar el archivo'
        );
      }
    });
  }

  downloadGuideImport() {
    this.noteService.downloadGuideImport().subscribe(data => {
      switch (data.type) {
        case HttpEventType.Response: {
          const downloadedFile = new Blob([data.body!], { type: data.body!.type });
          const a = document.createElement('a');
          a.setAttribute('style', 'display:none;');
          document.body.appendChild(a);
          a.download = "GuiaNotas.docx";
          a.href = URL.createObjectURL(downloadedFile);
          a.target = '_blank';
          a.click();
          document.body.removeChild(a);
          break;
        }
      }
    });
  }

  async getAllLists(...listsToLoad: Array<'companies' | 'branches' | 'enterprises'>) {
    try {
      const lists = {
        companies: async () => {
          this.listCompanies = await firstValueFrom(this.companyService.getAllCompaniesForSelectAsync());
        },
        branches: async () => {
          this.listBranches = await firstValueFrom(this.branchService.GetAllBranchesByCompanyIdForSelectAsync(this.companyId));
        },
        enterprises: async () => {
          this.listEnterprises = await firstValueFrom(this.enterpriseService.getEnterprisesByBranchIdForSelect(this.branchId));
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.branches(),
          lists.companies(),
          lists.enterprises()
        ]);
      }
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getBranch(branchId: number | null) {
    if (!branchId) {
      this.branchId = 0;
      this.branchCode = 0;
      this.listEnterprises = [];
      return;
    }

    const branch = this.listBranches.find(x => x.value == branchId);
    this.branchId = branchId;
    this.branchCode = branch.code;
    await this.getAllLists('enterprises');
  }

  async getCompany(companyId: number | null) {
    if (!companyId) {
      this.companyId = 0;
      this.companyCode = 0;
      this.listBranches = [];
      this.listEnterprises = [];
      this.validateForm();
      return;
    }

    const company = this.listCompanies.find(x => x.value == companyId);
    this.companyId = companyId;
    this.companyCode = company.code;
    await this.getAllLists('branches');
  }

  async getEnterprise(enterpriseId: number | null) {
    if (!enterpriseId) {
      this.enterpriseId = 0;
      this.enterpriseCode = 0;
      return;
    }

    const enterprise = this.listEnterprises.find(x => x.value == enterpriseId);
    this.enterpriseId = enterpriseId;
    this.enterpriseCode = enterprise.code;
  }

  getNameFile(file: any) {
    this.eventFile = file;
    const files = this.eventFile.target.files;
    if (files.length != 0) {
      for (let i = 0; i < files.length; i++) {
        this.nameFile = files[i].name;
      }
    } else {
      this.nameFile = 'Ningún archivo seleccionado.';
    }
  }

  async getPermission() {
    let url = this.router.url as string
    url = url.substring(1);
    this.url = url;
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getAllLists('companies'),
      this.getPermissionProfileByUrl(),
    ])
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos.', error);
    }
  }

  importNotes() {
    this.notesAlerts = [];
    this.errorNotes = 0;
    this.visibleTxt = false;
    if (!this.formImport.valid) {
      return this.showValidationMessages();
    }

    let isOk: boolean = true;
    const input = document.getElementById("fileImport") as any;
    const rowsImports = new Array<NoteImportModel>();
    let row: NoteImportModel;
    let counter: number = 1, bussinessDocumentsTypeCode: number, noteTypeId: number, consecutive: number,
      dateNote: Date, conceptCode: number, operationTypeDianCode: number, invoiceConsecutive: number,
      invoiceBussinessDocumentsTypeCode: number, cude: string, subtotalValue: number, ivaValue: number,
      totalValue: number, valueToPay: number, reteIvaValue: number, reteFuenteValue: number, reteIcaValue: number,
      conceptInvoicingCode: number, storeCode: number, value: number, articleCode: number, quantity: number,
      conceptIvaValue: number, conceptReteFuenteValue: number, conceptReteIcaValue: number;

    if (input.files[0]) {
      readXlsxFile(input.files[0]).then(async (rows) => {
        if (rows.length <= 1) {
          return this.toastHandlerService.showToast(
            'warning',
            `El archivo ingresado no tiene registros para importar`
          );
        }
        rows.splice(0, 1);
        rows.forEach(element => {
          counter = counter + 1;
          let rowHasError = false;

          const isDocumentSupport = element[4] as number === NoteTypeEnums.supportDocument;
          //A. Código Compañía:
          const companyCode = parseFloat(element[0] as string);
          if (isNaN(companyCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna A. El código de la Compañía no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }
          if (companyCode != this.companyCode) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna A. La Compañía del archivo no es igual a la Compañía seleccionada.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //B. Código Sucursal:
          const branchCode = parseFloat(element[1] as string);
          if (isNaN(branchCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna B. El código de la Sucursal no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }
          if (branchCode != this.branchCode) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna B. La Sucursal del archivo no es igual a la Sucursal seleccionada.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //C. Código Empresa:
          const enterpriseCode = parseFloat(element[2] as string);
          if (isNaN(enterpriseCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna C. El código de la Empresa no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }
          if (enterpriseCode != this.enterpriseCode) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna C. La Empresa del archivo no es igual a la Empresa seleccionada.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //D. Tipo de Documento Comercial:
          bussinessDocumentsTypeCode = parseFloat(element[3] as string);
          if (isNaN(bussinessDocumentsTypeCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna D. El tipo de documento comercial no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //E. Tipo Nota:
          noteTypeId = parseFloat(element[4] as string);
          if (isNaN(noteTypeId)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna E. El tipo nota no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //F. Número Nota:
          consecutive = parseFloat(element[5] as string);
          if (isNaN(consecutive)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna F. El número de nota no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //G. Fecha Nota:
          const dateNoteString = element[6] as string;
          if (dateNoteString == null || dateNoteString == "") {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna G. El campo de Fecha Nota es obligatorio.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //Convierte fecha de string a Date:
          dateNote = moment(dateNoteString, 'DD/MM/YYYY').toDate();
          if (!moment(dateNoteString, 'DD/MM/YYYY').isValid()) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna G. El campo de Fecha Nota no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //H. Código Concepto:
          conceptCode = parseFloat(element[7] as string);
          if (isNaN(conceptCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna H. El código concepto no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //I. Código Tipo Operación:
          operationTypeDianCode = parseFloat(element[8] as string);
          if (isNaN(operationTypeDianCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna I. El código tipo operación no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //J. Número de Factura:
          invoiceConsecutive = parseFloat(element[9] as string);
          if (isNaN(invoiceConsecutive)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna J. El número de factura no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //K. Tipo de Documento Comercial Factura:
          invoiceBussinessDocumentsTypeCode = parseFloat(element[10] as string);
          if (isNaN(invoiceBussinessDocumentsTypeCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna K. El tipo de documento comercial factura no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          // L. Cude:
          cude = element[11] as string;
          if (cude != null && cude.length > 99) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna L. El cude supera el tamaño máximo de caracteres (99)`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //M. Valor Subtotal:
          subtotalValue = parseFloat(element[12] as string);
          if (isNaN(subtotalValue)) {
            if (!isDocumentSupport) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}Columna M. El valor subtotal no es válido.`;
              this.notesAlerts.push(alert);
              rowHasError = true;
            }
            subtotalValue = 0;
          }

          //N. Valor Iva:
          ivaValue = parseFloat(element[13] as string);
          if (isNaN(ivaValue)) {
            if (!isDocumentSupport) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna N. El valor iva no es válido.`;
              this.notesAlerts.push(alert);
              rowHasError = true;
            }
            ivaValue = 0;
          }

          //O. Valor Total:
          totalValue = parseFloat(element[14] as string);
          if (isNaN(totalValue)) {
            if (!isDocumentSupport) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna O. El valor total no es válido.`;
              this.notesAlerts.push(alert);
              rowHasError = true;
            }
            totalValue = 0;
          }

          //P. Valor A Pagar:
          valueToPay = parseFloat(element[15] as string);
          if (isNaN(valueToPay)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna P. El valor a pagar no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //Q. Valor Retención Iva:
          reteIvaValue = parseFloat(element[16] as string);
          if (isNaN(reteIvaValue)) {
            if (!isDocumentSupport) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna Q. El valor retención iva no es válido.`;
              this.notesAlerts.push(alert);
              rowHasError = true;
            }
            reteIvaValue = 0;
          }

          //R. Valor Retención en la Fuente:
          reteFuenteValue = parseFloat(element[17] as string);
          if (isNaN(reteFuenteValue)) {
            if (!isDocumentSupport) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna R. El valor retención en la fuente no es válido.`;
              this.notesAlerts.push(alert);
              rowHasError = true;
            }
            reteFuenteValue = 0;
          }

          //S. Valor Retención Ica:
          reteIcaValue = parseFloat(element[18] as string);
          if (isNaN(reteIcaValue)) {
            if (!isDocumentSupport) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna S. El valor retención ica no es válido.`;
              this.notesAlerts.push(alert);
              rowHasError = true;
            }
            reteIcaValue = 0;
          }

          //T. Código de Concepto de Facturación:
          conceptInvoicingCode = parseFloat(element[19] as string);
          if (isNaN(conceptInvoicingCode)) {
            if (!isDocumentSupport) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna T. El código de concepto de facturación no es válido.`;
              this.notesAlerts.push(alert);
              rowHasError = true;
            }
            conceptInvoicingCode = 0;
          }

          //U. Código Bodega:
          storeCode = parseFloat(element[20] as string);
          if (isNaN(storeCode)) {
            if (!isDocumentSupport) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna U. El código bodega no es válido.`;
              this.notesAlerts.push(alert);
              rowHasError = true;
            }
            storeCode = 0;
          }

          //V. Valor Total:
          value = parseFloat(element[21] as string);
          if (isNaN(value)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna V. El valor total no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //Y. Valor Iva (Concepto):
          conceptIvaValue = parseFloat(element[24] as string);
          if (isNaN(conceptIvaValue)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna Y. El valor iva (concepto) no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //Z. Valor ReteFuente (Concepto):
          conceptReteFuenteValue = parseFloat(element[25] as string);
          if (isNaN(conceptReteFuenteValue)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna Z. El valor retefuente (concepto) no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //AA. Valor ReteIca (Concepto):
          conceptReteIcaValue = parseFloat(element[26] as string);
          if (isNaN(conceptReteIcaValue)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna AA. El valor reteica (concepto) no es válido.`;
            this.notesAlerts.push(alert);
            rowHasError = true;
          }

          //W. Código Artículo:
          if (noteTypeId === NoteTypeEnums.supportDocument) {
            const valueArticleCode = element[22];
            if (valueArticleCode == null || valueArticleCode === "") {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Columna W, Fila ${counter}. El código del artículo no es válido.`;
              this.notesAlerts.push(alert);
              rowHasError = true;
            }
            articleCode = Number(valueArticleCode);
            if (isNaN(articleCode)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Columna W, Fila ${counter}. El código del artículo no es válido.`;
              this.notesAlerts.push(alert);
              rowHasError = true;
            }

            //X. Cantidad
            const valueQuantity = element[23];
            if (valueQuantity == null || valueQuantity === "") {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Columna X, Fila ${counter}. La cantidad no es válida.`;
              this.notesAlerts.push(alert);
              rowHasError = true;
            }
            quantity = Number(valueQuantity);
            if (isNaN(quantity)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Columna X, Fila ${counter}. La cantidad no es válida.`;
              this.notesAlerts.push(alert);
              rowHasError = true;
            }
          }

          if (!rowHasError) {
            row = new NoteImportModel(counter, this.companyId!, this.branchId, this.enterpriseId, bussinessDocumentsTypeCode,
              noteTypeId, consecutive, dateNote, conceptCode, operationTypeDianCode, invoiceConsecutive, bussinessDocumentsTypeCode,
              cude, subtotalValue, ivaValue, totalValue, valueToPay, reteIvaValue, reteFuenteValue, reteIcaValue, conceptInvoicingCode,
              storeCode, value, articleCode, quantity, conceptIvaValue, conceptReteFuenteValue, conceptReteIcaValue);

            rowsImports.push(row);
          } else {
            isOk = false;
          }
        });

        if (isOk && rowsImports.length > 0) {
          try {
            const response = await firstValueFrom(this.noteService.importNotesAsync(rowsImports));
            this.loadPreInvoicingModel = response;
            this.notesAlerts = response?.alerts ?? [];
            this.errorNotes = response?.preInvoicingStateId;

            if (this.errorNotes === 1) {
              this.visibleTxt = true;
              this.toastHandlerService.showToast(
                'error',
                'La importación terminó con errores, puede exportarlos si lo desea.',
                'ERROR'
              );
            } else if (this.notesAlerts.length > 0) {
              this.visibleTxt = true;
              this.toastHandlerService.showToast(
                'error',
                'La importación terminó con alertas, puede exportarlas si lo desea'
              );
            } else {
              this.visibleTxt = false;
              this.toastHandlerService.showToast(
                'success',
                `La importación terminó exitosamente.`
              );
            }
            this.clearInputFile();
          } catch (error) {
            this.nameFile = 'Ningún archivo seleccionado.';
            console.error('Error al importar notas.', error);
          }
          (<HTMLInputElement>document.getElementById('fileImport')).value = '';
        } else {
          this.errorNotes = 1;
          this.visibleTxt = true;
          this.toastHandlerService.showToast(
            'error',
            'La importación terminó con errores, puede exportarlos si lo desea'
          );
        }
      });
    } else {
      this.toastHandlerService.showToast('warning', `No ha seleccionado ningún archivo.`);
    }
  }

  openToggle(event: any) {
    this.informationPopover.toggle(event);
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      companyId: {
        required: 'La compañía es obligatoria.',
        min: 'La compañía es obligatiria.',
      },
      branchId: {
        required: 'La sucursal es obligatoria.',
        min: 'La sucursal es obligatoria.'
      },
      enterpriseId: {
        required: 'La empresa es obligatoria.',
        min: 'La empresa es obligatoria.'
      },
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formImport.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm() {
    this.formImport = this.formBuilder.group({
      companyId: [0, [Validators.required, Validators.min(1)]],
      branchId: [0, [Validators.required, Validators.min(1)]],
      enterpriseId: [0, [Validators.required, Validators.min(1)]],
    });
  }
}
